import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  createNotificationTemplate,
  fetchNotificationTemplates,
  setNotificationTemplateActive,
  updateNotificationTemplate,
  type NotificationCategory,
  type NotificationTemplate,
} from '@/features/notifications/api';
import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_VARIABLE_ROWS,
} from '@/features/notifications/format';
import { ApiError } from '@/lib/api/http';

/** 画面から新規作成するときの分類（固定文面の system はシード専用） */
const CREATE_CATEGORY_OPTIONS: NotificationCategory[] = [
  'event',
  'survey',
  'message',
  'success',
  'score',
];

interface TemplateFormState {
  name: string;
  category: NotificationCategory;
  subject: string;
  body: string;
  senderName: string;
  variablesText: string;
}

const EMPTY_FORM: TemplateFormState = {
  name: '',
  category: 'event',
  subject: '',
  body: '',
  senderName: '',
  variablesText: '',
};

function parseVariables(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  );
}

function isSeededFixed(template: NotificationTemplate): boolean {
  return template.code === 'invitation' || template.code === 'password_reset';
}

/**
 * 業務マスター > 通知テンプレート（D-156 / D-291 / D-293）。
 * 課題・アンケートの設問マスタ（アンケートテンプレート）とは別画面。
 */
export function NotificationTemplatesPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['notification-templates'],
    queryFn: fetchNotificationTemplates,
  });

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [editForm, setEditForm] = useState({
    subject: '',
    body: '',
    senderName: '',
    category: 'event' as NotificationCategory,
  });
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<TemplateFormState>(EMPTY_FORM);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  function startEdit(template: NotificationTemplate): void {
    setEditing(template);
    setEditForm({
      subject: template.subject,
      body: template.body,
      senderName: template.sender_name ?? '',
      category: template.category,
    });
    setError(null);
  }

  function startCreate(): void {
    setCreateForm(EMPTY_FORM);
    setCreating(true);
    setError(null);
  }

  async function onSave(): Promise<void> {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await updateNotificationTemplate(editing.id, {
        subject: editForm.subject,
        body: editForm.body,
        sender_name: editForm.senderName || null,
        // 既定テンプレートは分類を変えられない。undefined を渡すのではなくキーごと送らない
        // （exactOptionalPropertyTypes では「undefined を渡す」と「省略」は別物）
        ...(isSeededFixed(editing) ? {} : { category: editForm.category }),
      });
      await queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setEditing(null);
      setNotice('テンプレートを保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createNotificationTemplate({
        name: createForm.name.trim(),
        category: createForm.category,
        subject: createForm.subject,
        body: createForm.body,
        sender_name: createForm.senderName || null,
        variables: parseVariables(createForm.variablesText),
      });
      await queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setCreating(false);
      setNotice('テンプレートを作成しました。通知プリセットのルールから選べます。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '作成に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleActive(template: NotificationTemplate): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setNotificationTemplateActive(template.id, !template.is_active);
      await queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<NotificationTemplate>[] = [
    { key: 'code', header: 'コード', render: (t) => t.code },
    { key: 'name', header: '名称', primary: true, render: (t) => t.name },
    {
      key: 'category',
      header: '分類',
      render: (t) => TEMPLATE_CATEGORY_LABELS[t.category] ?? t.category,
    },
    { key: 'subject', header: '件名', render: (t) => t.subject },
    { key: 'sender', header: '送信名義', render: (t) => t.sender_name ?? '—' },
    { key: 'is_active', header: '状態', render: (t) => (t.is_active ? '有効' : '無効化') },
  ];

  const createValid =
    createForm.name.trim() !== '' &&
    createForm.subject.trim() !== '' &&
    createForm.body.trim() !== '';

  return (
    <>
      <PageHeader
        title="通知テンプレート"
        description="メール等の件名・本文を管理します（課題・アンケートの設問は「アンケートテンプレート」）。会員招待・パスワード再設定はシステムがあらかじめ登録しています。差し込み変数は下表のとおりです。"
        actions={
          <button
            type="button"
            onClick={startCreate}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            新規作成
          </button>
        }
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <section className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <h2 className="mb-1 text-sm font-semibold text-wptext">差し込み変数</h2>
        <p className="mb-2 text-wptext-muted">
          件名・本文に <code>{'{{変数名}}'}</code> と書くと、送信時に実際の値へ置き換わります。
          一覧に無い変数や、その通知では埋まらない変数は空欄になります。
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">差し込み変数の一覧</caption>
            <thead>
              <tr className="border-b border-wpborder text-wptext-muted">
                <th className="px-2 py-1.5 font-medium">変数</th>
                <th className="px-2 py-1.5 font-medium">意味</th>
                <th className="px-2 py-1.5 font-medium">使える通知</th>
              </tr>
            </thead>
            <tbody>
              {TEMPLATE_VARIABLE_ROWS.map((row) => (
                <tr key={row.name} className="border-b border-wpborder last:border-0">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <code>{`{{${row.name}}}`}</code>
                  </td>
                  <td className="px-2 py-1.5">{row.meaning}</td>
                  <td className="px-2 py-1.5">{row.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ListTable<NotificationTemplate>
        caption="通知テンプレート一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(t) => t.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          { label: '編集する', onSelect: startEdit },
          { label: '無効化・有効化', onSelect: (t) => void onToggleActive(t) },
        ]}
        emptyTitle="テンプレートが登録されていません"
      />

      <Modal
        open={editing !== null}
        title={editing ? `${editing.name} の編集` : ''}
        onClose={() => setEditing(null)}
        wide
        footer={
          <button
            type="button"
            disabled={busy}
            onClick={() => void onSave()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            保存
          </button>
        }
      >
        {editing && (
          <div className="flex flex-col gap-2">
            {isSeededFixed(editing) ? (
              <p className="text-wptext-muted">
                分類: システム（固定メッセージ）。件名・本文のみ編集できます。
              </p>
            ) : (
              <label className="block">
                <span className="mb-1.5 block text-wptext-muted">分類</span>
                <select
                  value={editForm.category}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      category: e.target.value as NotificationCategory,
                    }))
                  }
                  className="rounded-sm border border-wpborder px-3 py-2"
                >
                  {CREATE_CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {TEMPLATE_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              <span className="mb-1.5 block text-wptext-muted">件名</span>
              <input
                value={editForm.subject}
                onChange={(e) => setEditForm((f) => ({ ...f, subject: e.target.value }))}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-wptext-muted">本文</span>
              <textarea
                value={editForm.body}
                onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
                rows={6}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-wptext-muted">送信名義（空欄で既定値）</span>
              <input
                value={editForm.senderName}
                onChange={(e) => setEditForm((f) => ({ ...f, senderName: e.target.value }))}
                className="rounded-sm border border-wpborder px-3 py-2"
              />
            </label>
            {editing.variables.length > 0 && (
              <p className="text-wptext-muted">
                使える差し込み変数: {editing.variables.map((v) => `{{${v}}}`).join(' ')}
                （詳細は画面上部の表）
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={creating}
        title="通知テンプレートの新規作成"
        onClose={() => setCreating(false)}
        wide
        footer={
          <button
            type="button"
            disabled={busy || !createValid}
            onClick={() => void onCreate()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            作成
          </button>
        }
      >
        <div className="flex flex-col gap-2">
          <p className="text-wptext-muted">
            コードは自動採番されます。会員招待・パスワード再設定は新規作成不要です（一覧にあります）。
          </p>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">分類</span>
            <select
              value={createForm.category}
              onChange={(e) =>
                setCreateForm((f) => ({
                  ...f,
                  category: e.target.value as NotificationCategory,
                }))
              }
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              {CREATE_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {TEMPLATE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">件名</span>
            <input
              value={createForm.subject}
              onChange={(e) => setCreateForm((f) => ({ ...f, subject: e.target.value }))}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">本文</span>
            <textarea
              value={createForm.body}
              onChange={(e) => setCreateForm((f) => ({ ...f, body: e.target.value }))}
              rows={6}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">送信名義（空欄で既定値）</span>
            <input
              value={createForm.senderName}
              onChange={(e) => setCreateForm((f) => ({ ...f, senderName: e.target.value }))}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">
              差し込み変数（カンマ区切り・任意。画面上部の表を参照）
            </span>
            <input
              value={createForm.variablesText}
              onChange={(e) => setCreateForm((f) => ({ ...f, variablesText: e.target.value }))}
              placeholder="event_title, starts_at"
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
        </div>
      </Modal>
    </>
  );
}
